import { Component } from 'react';
import styles from './ErrorBoundary.module.css';

/**
 * Catches rendering errors anywhere in its child tree.
 *
 * Two usage modes:
 *   <ErrorBoundary scope="app">     — full-screen fallback
 *   <ErrorBoundary scope="section"> — inline fallback inside a section pane
 *
 * Pass `resetKey` (any value) to auto-reset when it changes — useful for
 * section boundaries where you want to clear the error on route change.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log to console for now. When Sentry is wired:
    //   Sentry.captureException(error, { extra: errorInfo });
    console.error('[ErrorBoundary]', error, errorInfo);
    this.setState({ errorInfo });
  }

  componentDidUpdate(prevProps) {
    // Auto-reset on resetKey change (e.g. route change)
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.reset();
    }
  }

  reset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleGoHome = () => {
    window.location.href = '/clinics';
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const isAppScope = this.props.scope === 'app';
    const containerClass = isAppScope ? styles.appScope : styles.sectionScope;
    const errorMessage = this.state.error?.message || 'Unknown error';
    const errorStack = this.state.error?.stack || '';

    return (
      <div className={containerClass}>
        <div className={styles.card}>
          <div className={styles.icon}><i className="bi bi-exclamation-triangle" aria-hidden="true" /></div>
          <div className={styles.title}>
            {isAppScope ? 'Something went wrong' : 'This section ran into a problem'}
          </div>
          <div className={styles.subtitle}>
            {isAppScope
              ? "We've logged the issue. You can try again or go back to the home page."
              : "The rest of the app is still working. You can try again or navigate elsewhere."}
          </div>

          <details className={styles.details}>
            <summary className={styles.detailsSummary}>Error details</summary>
            <div className={styles.detailsBody}>
              <div className={styles.errorMessage}>{errorMessage}</div>
              {errorStack && (
                <pre className={styles.errorStack}>{errorStack}</pre>
              )}
            </div>
          </details>

          <div className={styles.actions}>
            <button type="button" className={styles.buttonGhost} onClick={this.reset}>
              Try again
            </button>
            {isAppScope ? (
              <button type="button" className={styles.buttonPrimary} onClick={this.handleReload}>
                Reload page
              </button>
            ) : (
              <button type="button" className={styles.buttonPrimary} onClick={this.handleGoHome}>
                Go to home
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
}